import * as React from "react"
import { DEHUMIDIFIER_IMAGE, PRODUCT_CATALOG } from "./checkout/catalogue"
import "./equipmentSelector.css"

type CatalogProduct = (typeof PRODUCT_CATALOG)[number]
type EquipmentType = CatalogProduct["type"]

const equipmentTypes: { type: EquipmentType; name: string; heading: string; description: string; image: string }[] = [
  { type: "dehumidifier", name: "Dehumidifiers", heading: "Choose your dehumidifier", description: "Dry out damp rooms and buildings", image: DEHUMIDIFIER_IMAGE },
  { type: "portable-ac", name: "Air conditioners", heading: "Choose your air conditioner", description: "Portable cooling for homes and offices", image: "/products/homcom-portable-ac.jpg" },
  { type: "jetwash", name: "Jet washers", heading: "Choose your jet washer", description: "Clean patios, driveways and more", image: "/products/karcher-k4.jpg" },
]

const modelDescriptions: Partial<Record<CatalogProduct["sku"], string>> = {
  ecd30: "For everyday damp and small leaks",
  ecd50: "For water leaks and renovations",
  ecd70: "For construction and flooding",
}

const selectionStateKey = "arcticHireEquipmentType"

function selectedTypeFromHistory(): EquipmentType | null {
  const value: unknown = window.history.state?.[selectionStateKey]
  return equipmentTypes.find(item => item.type === value)?.type ?? null
}

function bookingHref(product: CatalogProduct): string {
  const url = new URL(window.location.href)
  url.searchParams.set("type", product.type)
  url.searchParams.set("sku", product.sku)
  url.searchParams.set("step", "dates")
  url.searchParams.delete("product")
  return `${url.pathname}${url.search}${url.hash}`
}

function Arrow({ back = false }: { back?: boolean }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={back ? "M19 12H5m7-7-7 7 7 7" : "M5 12h14m-7-7 7 7-7 7"} /></svg>
}

export function EquipmentSelector() {
  const [selectedType, setSelectedType] = React.useState(selectedTypeFromHistory)
  const heading = React.useRef<HTMLHeadingElement>(null)
  const previousType = React.useRef(selectedType)
  const headingId = React.useId()
  const selected = equipmentTypes.find(item => item.type === selectedType)

  React.useEffect(() => {
    const pop = () => setSelectedType(selectedTypeFromHistory())
    window.addEventListener("popstate", pop)
    return () => window.removeEventListener("popstate", pop)
  }, [])

  React.useEffect(() => {
    if (previousType.current === selectedType) return
    previousType.current = selectedType
    heading.current?.focus({ preventScroll: true })
    window.scrollTo({ top: 0, behavior: "instant" })
  }, [selectedType])

  function chooseType(type: EquipmentType | null) {
    if (type === selectedType) return
    // Keep browsing history separate from the booking form's step history.
    // A reload restores this screen; choosing a model opens its normal URL.
    window.history.pushState({ ...window.history.state, [selectionStateKey]: type }, "")
    setSelectedType(type)
  }

  return (
    <div className={`equipment-selector${selected ? " equipment-selector-models" : ""}`}>
      {selected && <button type="button" className="equipment-back" onClick={() => chooseType(null)}><Arrow back />Change equipment type</button>}
      <section className="equipment-panel" aria-labelledby={headingId}>
        <h2 ref={heading} id={headingId} tabIndex={-1}>{selected?.heading ?? "What would you like to hire?"}</h2>
        {selected ? (
          <div className="equipment-model-list">
            {PRODUCT_CATALOG.filter(product => product.type === selected.type).map(product => (
              <a key={product.slug} className="equipment-model" href={bookingHref(product)} aria-label={`Select ${product.name}`}>
                <img src={product.image} alt="" />
                <span className="equipment-model-copy">
                  <strong>{product.name}</strong>
                  {modelDescriptions[product.sku] && <small>{modelDescriptions[product.sku]}</small>}
                </span>
                <Arrow />
              </a>
            ))}
          </div>
        ) : (
          <div className="equipment-type-grid">
            {equipmentTypes.map(item => {
              const count = PRODUCT_CATALOG.filter(product => product.type === item.type).length
              return <button key={item.type} type="button" className="equipment-type" onClick={() => chooseType(item.type)} aria-label={`${item.name}, ${count} models`}>
                <img src={item.image} alt="" />
                <span className="equipment-type-copy">
                  <strong>{item.name}</strong>
                  <small>{item.description}</small>
                  <span className="equipment-type-mobile-count" aria-hidden="true">{count} models</span>
                </span>
                <span className="equipment-type-next"><span>View {count} models</span><Arrow /></span>
              </button>
            })}
          </div>
        )}
      </section>
    </div>
  )
}
